import React, { useState, useRef, useEffect } from "react";
import { API_URLS } from "../../../apiUrl";
import BoroughDropdown from "../../customHooks/boroughDropdown";
import WardDropdown from "../../customHooks/wardDropdown";
import axiosInstance from "../../../components/axiosInstance";

interface FormData {
  municipalityTbl_id: string;
  borough_tbl_id: string;
  ward_tbl_id: string;
  street_name: string;
  file: File | null;
}

interface AddStreetProps {
  muniCipalityName: string;
  muniCipalityId: string;
}

const AddStreet: React.FC<AddStreetProps> = ({ muniCipalityName, muniCipalityId }) => {
  const [formData, setFormData] = useState<FormData>({
    municipalityTbl_id: muniCipalityId,
    borough_tbl_id: "",
    ward_tbl_id: "",
    street_name: "",
    file: null,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState("");
  const [dataExistMessage, setDataExistMessage] = useState("");
  const [dataFailedMessage, setDataFailedMessage] = useState("");
  const [streetNames, setStreetNames] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isSampleDownloaded, setIsSampleDownloaded] = useState(false);

  const formRef = useRef<HTMLFormElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === "file") {
      const fileInput = e.target as HTMLInputElement;
      setFormData((prev) => ({
        ...prev,
        [name]: fileInput.files ? fileInput.files[0] : null,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      municipalityTbl_id: muniCipalityId,
      borough_tbl_id: "",
      ward_tbl_id: "",
      street_name: "",
      file: null,
    });
    setErrors({});
    setStreetNames([]);
    setIsSampleDownloaded(false);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.borough_tbl_id) newErrors.borough_tbl_id = "Borough is required";
    if (!formData.ward_tbl_id) newErrors.ward_tbl_id = "Ward No. is required";
    if (streetNames.length === 0) newErrors.street_name = "Street Name is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const submitData = {
      ...formData,
      street_name: streetNames.join("#"),
    };

    try {
      const response = await axiosInstance.post(API_URLS.addStreet, submitData);
      const { addedStreets, duplicateStreets } = response.data.data;

      if (addedStreets?.length > 0) {
        const streetNamesList = addedStreets.map((street: any) => street.street_name).join(", ");
        setSuccessMessage(`Street inserted successfully: ${streetNamesList}`);
      } else if (duplicateStreets?.length > 0) {
        setDataExistMessage("Street already exists for this municipality!");
      } else {
        setDataFailedMessage("Street add failed!");
      }

      resetForm();
    } catch (error) {
      console.error("Error while adding street:", error);
      setDataFailedMessage("Street add failed.");
    } finally {
      clearMessagesAfterDelay();
    }
  };

  const clearMessagesAfterDelay = () => {
    setTimeout(() => {
      setSuccessMessage("");
      setDataExistMessage("");
      setDataFailedMessage("");
    }, 5000);
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (formRef.current && !formRef.current.contains(e.target as Node)) {
      setErrors({});
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [, , , boroughList] = BoroughDropdown(muniCipalityId);
  const [, , , wardList] = WardDropdown(muniCipalityId, formData.borough_tbl_id);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && inputValue.trim()) {
      const newStreet = inputValue.trim();
      if (!streetNames.includes(newStreet)) {
        setStreetNames((prev) => [...prev, newStreet]);
      }
      setInputValue("");
      event.preventDefault();
    }
  };

  const handleRemoveStreet = (streetToRemove: string) => {
    setStreetNames((prev) => prev.filter((name) => name !== streetToRemove));
  };

  const downloadSampleCSV = () => {
    if (!formData.borough_tbl_id) {
      alert("Please select a borough before downloading the sample CSV.");
      return;
    }
    if (!formData.ward_tbl_id) {
      alert("Please select a ward before downloading the sample CSV.");
      return;
    }

    const csvContent = [
      ["municipality", "borough_id", "ward_id", "street_name"],
      [muniCipalityId, formData.borough_tbl_id, formData.ward_tbl_id, "Street 001"],
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "street_bulk_data.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setIsSampleDownloaded(true);
  };

  const handleCSVUpload = async () => {
    if (!formData.file) {
      setDataFailedMessage("Please select a file.");
      return;
    }

    const formDataForUpload = new FormData();
    formDataForUpload.append("street_bulk_data", formData.file);

    try {
      const response = await axiosInstance.post(API_URLS.BulkStreetData, formDataForUpload);

      if (response.data.message) {
        setSuccessMessage("CSV uploaded successfully!");
      } else {
        setDataFailedMessage("CSV upload failed.");
      }
    } catch (error) {
      console.error("Error while uploading CSV:", error);
      setDataFailedMessage("CSV upload failed.");
    } finally {
      clearMessagesAfterDelay();
    }
  };

  return (
    <div className="max-w-full mx-auto">
      <div className="flex flex-col gap-5">
        <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
          Add Street
        </h1>

        <div className="p-6 bg-white rounded-lg shadow-lg dark:bg-gray-800">
          <form ref={formRef} onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2">
              {/* Municipality Field */}
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Municipality <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={muniCipalityName}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  disabled
                />
              </div>

              {/* Borough Dropdown */}
              <div>
                <label htmlFor="borough_tbl_id" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Borough <span className="text-red-600">*</span>
                </label>
                <select
                  id="borough_tbl_id"
                  name="borough_tbl_id"
                  value={formData.borough_tbl_id}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">Select Borough</option>
                  {boroughList?.map((borough: any) => (
                    <option key={borough.borough_primary_id} value={borough.borough_primary_id}>
                      {borough.borough_name}
                    </option>
                  ))}
                </select>
                {errors.borough_tbl_id && (
                  <p className="text-red-600 text-sm mt-1">{errors.borough_tbl_id}</p>
                )}
              </div>

              {/* Ward Dropdown */}
              <div>
                <label htmlFor="ward_tbl_id" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Ward <span className="text-red-600">*</span>
                </label>
                <select
                  id="ward_tbl_id"
                  name="ward_tbl_id"
                  value={formData.ward_tbl_id}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">Select Ward</option>
                  {wardList?.map((ward: any) => (
                    <option key={ward.ward_primary_id} value={ward.ward_primary_id}>
                      {ward.ward_no}
                    </option>
                  ))}
                </select>
                {errors.ward_tbl_id && (
                  <p className="text-red-600 text-sm mt-1">{errors.ward_tbl_id}</p>
                )}
              </div>

              {/* Street Name Input */}
              <div>
                <label className="flex gap-1 mb-2 text-sm font-medium text-gray-900 dark:text-white leading-3">
                  Street Name <span className="text-red-600">*</span>
                  <span className="text-red-500 block text-xs font-medium leading-3">
                    (Type the street names and press Enter)
                  </span>
                </label>
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap gap-2">
                    {streetNames.map((street, index) => (
                      <div
                        key={index}
                        className="bg-blue-200 text-blue-800 px-3 py-1 rounded-full flex items-center gap-1"
                      >
                        {street}
                        <button
                          type="button"
                          onClick={() => handleRemoveStreet(street)}
                          className="text-red-500 text-sm hover:text-red-700"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  <input
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyPress}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="Type a street name and press Enter"
                  />
                  {errors.street_name && (
                    <p className="text-red-600 text-sm mt-1">{errors.street_name}</p>
                  )}
                </div>
              </div>

              {/* File Upload */}
              <div>
                <label htmlFor="file-upload" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Bulk Upload (CSV, Excel)
                </label>
                <div className="flex items-center">
                  <input
                    type="file"
                    id="file-upload"
                    name="file"
                    accept=".csv, .xls, .xlsx"
                    onChange={handleChange}
                    disabled={!isSampleDownloaded || streetNames.length > 0}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                  {formData.file && (
                    <span className="ml-4 text-gray-700 dark:text-gray-400">
                      {formData.file.name}
                    </span>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="lg:col-span-2 sm:col-span-2 mt-3">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={downloadSampleCSV}
                    disabled={streetNames.length > 0 || isSampleDownloaded}
                    className="text-white bg-orange-500 hover:bg-orange-600 focus:ring-4 focus:outline-none focus:ring-orange-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center disabled:opacity-50"
                  >
                    Download Sample CSV
                  </button>

                  <button
                    type="button"
                    onClick={handleCSVUpload}
                    disabled={!formData.borough_tbl_id || !isSampleDownloaded || !formData.file}
                    className="text-white bg-orange-500 hover:bg-orange-600 focus:ring-4 focus:outline-none focus:ring-orange-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center disabled:opacity-50"
                  >
                    Upload CSV
                  </button>

                  <button
                    type="submit"
                    disabled={!!formData.file}
                    className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center disabled:opacity-50"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </form>

          {/* Success Message */}
          {successMessage && (
            <div className="mt-4 p-4 bg-green-600 text-white rounded-lg flex justify-between items-center">
              <span>{successMessage}</span>
              <button
                onClick={() => setSuccessMessage("")}
                className="ml-4 text-white font-bold text-lg focus:outline-none"
              >
                ×
              </button>
            </div>
          )}

          {/* Warning Message */}
          {dataExistMessage && (
            <div className="mt-4 p-4 bg-yellow-500 text-black rounded-lg flex justify-between items-center">
              <span>{dataExistMessage}</span>
              <button
                onClick={() => setDataExistMessage("")}
                className="ml-4 text-black font-bold text-lg focus:outline-none"
              >
                ×
              </button>
            </div>
          )}

          {/* Error Message */}
          {dataFailedMessage && (
            <div className="mt-4 p-4 bg-red-500 text-white rounded-lg flex justify-between items-center">
              <span>{dataFailedMessage}</span>
              <button
                onClick={() => setDataFailedMessage("")}
                className="ml-4 text-white font-bold text-lg focus:outline-none"
              >
                ×
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddStreet;